export type ReleaseUpdateDecision = "current" | "defer" | "unversioned";

type ReleaseMarker = {
  commit?: string;
};

const RELEASE_MARKER_URL = "/release.json";
const RELEASE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const bundledRelease = process.env.REACT_APP_JELLYFLIX_RELEASE ?? "";

export function decideReleaseUpdate(
  runningRelease: string,
  publishedRelease: string,
  _pathname: string,
  _initialCheck: boolean,
): ReleaseUpdateDecision {
  if (!runningRelease || !publishedRelease) return "unversioned";
  if (runningRelease === publishedRelease) return "current";
  return "defer";
}

/**
 * Checks a tiny deployment marker without invalidating hashed application
 * assets. Releases discovered by an active SPA are recorded but never interrupt
 * the current page. The next real document load revalidates index.html and
 * receives the new hashed bundle.
 */
export function startReleaseMonitor() {
  if (!bundledRelease || typeof window === "undefined") return () => undefined;

  let initialCheck = true;
  let checking = false;
  let stopped = false;
  const check = async () => {
    if (checking || stopped) return;
    checking = true;
    try {
      const response = await fetch(RELEASE_MARKER_URL, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const marker = await response.json() as ReleaseMarker;
      const publishedRelease = marker.commit?.trim() ?? "";
      const decision = decideReleaseUpdate(
        bundledRelease,
        publishedRelease,
        window.location.pathname,
        initialCheck,
      );
      if (decision === "defer") {
        window.sessionStorage.setItem("jellyflix.pending-release", publishedRelease);
      } else if (decision === "current") {
        window.sessionStorage.removeItem("jellyflix.pending-release");
      }
    } catch {
      // Release discovery must never block startup or take the app offline.
    } finally {
      checking = false;
      initialCheck = false;
    }
  };

  const onFocus = () => { void check(); };
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") void check();
  };
  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisibilityChange);
  const interval = window.setInterval(() => { void check(); }, RELEASE_CHECK_INTERVAL_MS);
  void check();

  return () => {
    stopped = true;
    window.clearInterval(interval);
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
